import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SvcPostPublic, SvcGetPrivate, SvcPutPrivate, setSession } from '../services/userService.js';
import { toast } from 'react-toastify';

function RegistrationForm() {
  const navigate = useNavigate();
  
  // Step Management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Form Data
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    password: '',
    fullName: '',
    age: '',
    weight: '',
    heightFeet: '',
    heightInch: '',
    gender: '',
    diet: '',
    location: '',
    privacy: false
  });

  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // OTP States
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Disable browser back
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    const handlePop = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  // OTP Timer
  useEffect(() => {
    if (currentStep === 2 && timer > 0) {
      const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer, currentStep]);

  // Focus OTP first input
  useEffect(() => {
    if (currentStep === 2) {
      inputRefs.current[0]?.focus();
    }
  }, [currentStep]);

  // Auto location fetch on step 3
  useEffect(() => {
    if (currentStep === 3 && !formData.location) {
      fetchLocation();
    }
  }, [currentStep]);

  // VALIDATION
  const validate = (name, value) => {
    switch (name) {
      case 'email': return /\S+@\S+\.\S+/.test(value);
      case 'mobile': return value.replace(/\D/g, '').length === 10;
      case 'password': return value.length >= 6;
      case 'fullName': return value.trim().length >= 2;
      case 'age': return parseInt(value) >= 1 && parseInt(value) <= 120;
      case 'weight': return parseInt(value) >= 1 && parseInt(value) <= 500;

      case 'height': {
        const ft = parseInt(formData.heightFeet);
        const inch = parseInt(formData.heightInch);
        return ft > 0 && inch >= 0 && inch < 12;
      }

      case 'gender': return value !== '';
      case 'diet': return value !== '';
      case 'location': return value.trim().length >= 2;
      case 'privacy': return value === true;
      default: return true;
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));

    if (name === "heightFeet" || name === "heightInch") {
      setErrors(prev => ({ ...prev, height: !validate("height") }));
    } else {
      setErrors(prev => ({ ...prev, [name]: !validate(name, value) }));
    }
  };

  const getInputClass = (name) => {
    if (!touched[name]) return "reg-input";
    return `reg-input ${errors[name] || (name.includes("height") && errors.height) ? "input-error" : "input-success"}`;
  };

  const getIcon = (name) => {
    if (!touched[name]) return null;
    const isError = errors[name] || (name.includes("height") && errors.height);
    return isError ?
      <span className="material-symbols-outlined status-icon error">error</span> :
      <span className="material-symbols-outlined status-icon success">check_circle</span>;
  };

  // LOCATION FETCH
  const fetchLocation = () => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported.");
      return;
    }

    setIsLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        const loc = `${lat}, ${lng}`;
        setFormData(prev => ({ ...prev, location: loc }));
        setErrors(prev => ({ ...prev, location: false }));
        setTouched(prev => ({ ...prev, location: true }));
        setIsLoadingLocation(false);
        setLocationError("");
      },
      (err) => {
        setIsLoadingLocation(false);
        let msg = "Unable to detect location";
        if (err.code === 1) msg = "Location permission denied";
        if (err.code === 2) msg = "Location unavailable";
        if (err.code === 3) msg = "Location request timeout";
        setLocationError(msg);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  // STEP 1 SUBMIT
  const isStep1Valid = () => {
    return validate('email', formData.email) &&
      validate('mobile', formData.mobile) &&
      validate('password', formData.password);
  };

  const handleStep1Submit = () => {
    const errs = {
      email: !validate("email", formData.email),
      mobile: !validate("mobile", formData.mobile),
      password: !validate("password", formData.password)
    };
    setErrors(errs);
    setTouched({ email: true, mobile: true, password: true });

    if (Object.values(errs).includes(true)) {
      toast.error("Please fill all fields correctly");
      return;
    }

    setIsLoading(true);

    SvcPostPublic("/users/send-otp", { mobile: formData.mobile })
      .then(res => {
        toast.success(res.data?.message || "OTP sent!");
        setCurrentStep(2);
        setTimer(30);
        setCanResend(false);
      })
      .catch(err => {
        toast.error(err?.response?.data?.message || "Failed to send OTP");
      })
      .finally(() => setIsLoading(false));
  };

  // OTP CHANGE FUNCTIONS
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.split("").slice(0, 6);
      const upd = [...otp];
      digits.forEach((d, i) => {
        if (index + i < 6 && /^\d$/.test(d)) {
          upd[index + i] = d;
        }
      });
      setOtp(upd);
      const next = Math.min(index + digits.length, 5);
      inputRefs.current[next]?.focus();
      setOtpError("");
      return;
    }

    if (value && !/^\d$/.test(value)) return;

    const upd = [...otp];
    upd[index] = value;
    setOtp(upd);
    setOtpError("");

    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(paste)) {
      const upd = paste.split("").concat(Array(6 - paste.length).fill(""));
      setOtp(upd);
      const next = Math.min(paste.length, 5);
      inputRefs.current[next]?.focus();
      setOtpError("");
    }
  };

  const isOtpComplete = otp.every(x => x !== "");

  // OTP VERIFY
  const handleOtpVerify = () => {
    if (!isOtpComplete) {
      setOtpError("Please enter all 6 digits");
      return;
    }

    setIsLoading(true);

    SvcPostPublic("/users/validate-otp", {
      mobile: formData.mobile,
      otp: otp.join(""),
      email: formData.email,
      password: formData.password,
      fullName: formData.fullName || ''
    })
      .then(res => {
        if (res.data?.status === true) {
          toast.success("OTP Verified!");

          if (res.data?.token && res.data?._id) {
            setSession({ token: res.data.token, _id: res.data._id });
          }

          SvcGetPrivate("/users/profile")
            .then(pr => {
              const user = pr.data.record;

              if (user.firstName && user.birth_year && user.gender && user.weight && user.height) {
                navigate("/dashboard", { replace: true });
              } else {
                const cy = new Date().getFullYear();
                const calcAge = user.birth_year ? cy - parseInt(user.birth_year) : "";
                setFormData(prev => ({
                  ...prev,
                  fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                  age: calcAge,
                  gender: user.gender || "",
                  weight: user.weight || "",
                  heightFeet: "",
                  heightInch: "",
                  location: user.location || "",
                  diet: user.diet || ""
                }));
                setCurrentStep(3);
              }
            })
            .catch(() => {
              setCurrentStep(3);
            });

        } else {
          setOtpError(res.data?.message || "Invalid OTP");
          setOtp(['','','','','','']);
          inputRefs.current[0]?.focus();
        }
      })
      .catch(err => {
        setOtpError(err?.response?.data?.message || "Verification failed");
        toast.error(err?.response?.data?.message || "Verification failed");
        setOtp(['','','','','','']);
        inputRefs.current[0]?.focus();
      })
      .finally(() => setIsLoading(false));
  };

  const handleResendOtp = () => {
    setIsLoading(true);
    SvcPostPublic("/users/send-otp", { mobile: formData.mobile })
      .then(() => {
        toast.success("OTP resent!");
        setTimer(30);
        setCanResend(false);
        setOtp(['','','','','','']);
        setOtpError("");
        inputRefs.current[0]?.focus();
      })
      .catch(() => toast.error("Failed to resend OTP"))
      .finally(() => setIsLoading(false));
  };

  // STEP 3 SUBMIT
  const isStep3Valid = () => {
    return validate('fullName', formData.fullName) &&
      validate('age', formData.age) &&
      validate('weight', formData.weight) &&
      validate('height') &&
      validate('gender', formData.gender) &&
      validate('diet', formData.diet) &&
      validate('location', formData.location) &&
      validate('privacy', formData.privacy);
  };

  const handleStep3Submit = () => {
    const ft = parseFloat(formData.heightFeet || 0);
    const inch = parseFloat(formData.heightInch || 0);

    const heightCm = Math.round((ft * 30.48) + (inch * 2.54));

    const errs = {
      fullName: !validate("fullName", formData.fullName),
      age: !validate("age", formData.age),
      weight: !validate("weight", formData.weight),
      height: !validate("height"),
      gender: !validate("gender", formData.gender),
      diet: !validate("diet", formData.diet),
      location: !validate("location", formData.location),
      privacy: !validate("privacy", formData.privacy)
    };
    setErrors(errs);
    setTouched({
      fullName: true, age: true, weight: true, height: true,
      gender: true, diet: true, location: true, privacy: true
    });

    if (Object.values(errs).includes(true)) {
      toast.error("Please fill all fields correctly");
      return;
    }

    setIsLoading(true);

    const payload = {
      fullName: formData.fullName,
      age: formData.age,
      weight: formData.weight,
      height: heightCm,
      gender: formData.gender,
      location: formData.location,
      diet: formData.diet
    };

    SvcPutPrivate("/users/profile", payload)
      .then(() => {
        toast.success("Registration completed successfully!");
        navigate("/wellness-guidance", { replace: true });
      })
      .catch(err => {
        toast.error(err?.response?.data?.message || "Registration failed");
      })
      .finally(() => setIsLoading(false));
  };

  // BACK HANDLER
  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setOtp(['','','','','','']);
      setOtpError('');
    } else if (currentStep === 3) {
      setCurrentStep(2);
    }
  };

  return (
    <section className="reg-section">
      <div className="reg-card">

        {/* HEADER */}
        <header className="reg-header">
          <div className="back-btn" onClick={handleBack}>
            <span className="material-symbols-outlined">arrow_back</span>
          </div>
          <h1 className="header-text">
            {currentStep === 1 ? 'Create Account' :
             currentStep === 2 ? 'Verify Your Number' :
             'Complete Your Profile'}
          </h1>
          <div className="header-spacer"></div>
        </header>

        {/* STEP DOTS */}
        <div className="progress-bar">
          <div className={`progress-dot ${currentStep >= 1 ? 'active' : ''}`}></div>
          <div className={`progress-dot ${currentStep >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-dot ${currentStep >= 3 ? 'active' : ''}`}></div>
        </div>

        {/* ======================== STEP 1 ======================= */}
        {currentStep === 1 && (
          <>
            <main className="reg-main">
              <h2 className="reg-title">Login Info</h2>
              <p className="reg-subtitle">Create your account credentials</p>

              {/* EMAIL */}
              <div className="form-group">
                <label className="form-label">Email</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    className={getInputClass('email')}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                  {getIcon('email')}
                </div>
                {touched.email && errors.email && <p className="error-text">Valid email required</p>}
              </div>

              {/* MOBILE */}
              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <div className="input-wrapper">
                  <input
                    type="tel"
                    className={getInputClass('mobile')}
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                  />
                  {getIcon('mobile')}
                </div>
                {touched.mobile && errors.mobile && <p className="error-text">10-digit mobile required</p>}
              </div>

              {/* PASSWORD */}
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={getInputClass('password')}
                    placeholder="Create password (min 6 chars)"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                  />
                  <span
                    className="material-symbols-outlined password-toggle-icon"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                  {getIcon('password')}
                </div>
                {touched.password && errors.password && <p className="error-text">Min 6 characters required</p>}
              </div>
            </main>

            <footer className="reg-footer">
              <button
                className={`submit-btn ${isStep1Valid() ? 'active' : ''}`}
                disabled={!isStep1Valid() || isLoading}
                onClick={handleStep1Submit}
              >
                {isLoading ? 'Sending OTP...' : 'Continue'}
              </button>
              <p className="login-text">
                Already have an account? <span onClick={() => navigate('/login')}>Log In</span>
              </p>
            </footer>
          </>
        )}

        {/* ======================== STEP 2 ======================= */}
        {currentStep === 2 && (
          <>
            <main className="otp-main">
              <h2 className="otp-title">Enter Verification Code</h2>
              <p className="otp-subtitle">
                We've sent a 6-digit code to <span className="phone-number">{formData.mobile}</span>
              </p>

              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    className={`otp-input ${digit ? 'filled' : ''} ${otpError ? 'error' : ''}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>

              {otpError && (
                <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '-16px' }}>
                  {otpError}
                </p>
              )}
            </main>

            <footer className="otp-footer">
              <p className="resend-text">
                {!canResend ? (
                  <>Didn't receive code? Resend in <span style={{ color: 'var(--primary)', fontWeight: '700' }}>{timer}s</span></>
                ) : (
                  <>Didn't receive code? <span className="resend-link" onClick={handleResendOtp}>Resend</span></>
                )}
              </p>
              <button
                className={`verify-btn ${isOtpComplete && !isLoading ? 'active' : ''}`}
                onClick={handleOtpVerify}
                disabled={!isOtpComplete || isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </footer>
          </>
        )}

        {/* ======================== STEP 3 ======================= */}
        {currentStep === 3 && (
          <>
            <main className="reg-main">
              <h2 className="reg-title">Personal Info</h2>
              <p className="reg-subtitle">Tell us about yourself</p>

              {/* FULL NAME */}
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className={getInputClass('fullName')}
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                  />
                  {getIcon('fullName')}
                </div>
                {touched.fullName && errors.fullName && <p className="error-text">Name required</p>}
              </div>

              {/* AGE + WEIGHT */}
              <div className="row">
                <div className="form-group">
                  <label className="form-label">Age</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      className={getInputClass('age')}
                      placeholder="e.g. 25"
                      value={formData.age}
                      onChange={(e) => handleChange('age', e.target.value)}
                    />
                    {getIcon('age')}
                  </div>
                  {touched.age && errors.age && <p className="error-text">Valid age required</p>}
                </div>

                <div className="form-group">
                  <label className="form-label">Weight (kg)</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      className={getInputClass('weight')}
                      placeholder="e.g. 70"
                      value={formData.weight}
                      onChange={(e) => handleChange('weight', e.target.value)}
                    />
                    {getIcon('weight')}
                  </div>
                  {touched.weight && errors.weight && <p className="error-text">Weight required</p>}
                </div>
              </div>

              {/* HEIGHT (FEET + INCHES DROPDOWN) */}
              <div className="form-group">
                <label className="form-label">Height</label>

                <div className="row">

                  {/* Feet Dropdown */}
                  <div className="form-group">
                    <div className="select-wrapper">
                      <select
                        className={getInputClass('heightFeet')}
                        value={formData.heightFeet}
                        onChange={(e) => handleChange('heightFeet', e.target.value)}
                      >
                        <option value="">Feet</option>
                        {[3,4,5,6,7].map(ft => (
                          <option key={ft} value={ft}>{ft} ft</option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined select-arrow">expand_more</span>
                    </div>
                  </div>

                  {/* Inches Dropdown */}
                  <div className="form-group">
                    <div className="select-wrapper">
                      <select
                        className={getInputClass('heightInch')}
                        value={formData.heightInch}
                        onChange={(e) => handleChange('heightInch', e.target.value)}
                      >
                        <option value="">Inches</option>
                        {Array.from({ length: 12 }, (_, i) => i).map(inch => (
                          <option key={inch} value={inch}>{inch} in</option>
                        ))}
                      </select>
                      <span className="material-symbols-outlined select-arrow">expand_more</span>
                    </div>
                  </div>

                </div>

                {(touched.heightFeet || touched.heightInch) && errors.height && (
                  <p className="error-text">Valid height required</p>
                )}
              </div>

              {/* GENDER */}
              <div className="form-group">
                <label className="form-label">Gender</label>
                <div className="gender-grid">
                  {['Male', 'Female', 'Other'].map((g) => (
                    <button
                      key={g}
                      type="button"
                      className={`gender-btn ${formData.gender === g.toLowerCase() ? 'active' : ''}`}
                      onClick={() => handleChange('gender', g.toLowerCase())}
                    >
                      {g}
                    </button>
                  ))}
                </div>
                {touched.gender && errors.gender && <p className="error-text">Gender required</p>}
              </div>

              {/* DIET */}
              <div className="form-group">
                <label className="form-label">Diet Preference</label>
                <div className="select-wrapper">
                  <select
                    className={getInputClass('diet')}
                    value={formData.diet}
                    onChange={(e) => handleChange('diet', e.target.value)}
                  >
                    <option value="">Select diet preference</option>
                    <option value="omnivore">Omnivore</option>
                    <option value="vegetarian">Vegetarian</option>
                    <option value="vegan">Vegan</option>
                    <option value="pescatarian">Pescatarian</option>
                    <option value="keto">Keto</option>
                  </select>
                  <span className="material-symbols-outlined select-arrow">expand_more</span>
                </div>
                {touched.diet && errors.diet && <p className="error-text">Diet required</p>}
              </div>

              {/* LOCATION */}
              <div className="form-group">
                <label className="form-label">Location</label>
                <div className="input-wrapper has-left-icon">
                  <span className="material-symbols-outlined left-icon">location_on</span>
                  <input
                    type="text"
                    className={getInputClass('location')}
                    placeholder="Your location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                  />
                  <span
                    className="material-symbols-outlined location-fetch-icon"
                    onClick={fetchLocation}
                  >
                    {isLoadingLocation ? 'progress_activity' : 'my_location'}
                  </span>
                  {getIcon('location')}
                </div>
                {locationError && <p className="error-text">{locationError}</p>}
                {touched.location && errors.location && <p className="error-text">Location required</p>}
              </div>

              {/* PRIVACY */}
              <div className="checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={formData.privacy}
                  onChange={(e) => handleChange('privacy', e.target.checked)}
                />
                <span className={touched.privacy && errors.privacy ? 'checkbox-error' : ''}>
                  I agree to the <a href="#">Data Privacy Policy</a>
                </span>
              </div>
              {touched.privacy && errors.privacy && (
                <p className="error-text" style={{ marginLeft: "32px" }}>Agreement required</p>
              )}
            </main>

            <footer className="reg-footer">
              <button
                className={`submit-btn ${isStep3Valid() ? 'active' : ''}`}
                disabled={!isStep3Valid() || isLoading}
                onClick={handleStep3Submit}
              >
                {isLoading ? 'Completing...' : 'Complete Registration'}
              </button>
            </footer>
          </>
        )}

      </div>
    </section>
  );
}

export default RegistrationForm;
